/**
 * The words GET /v1/dashboard/home sends, in the active language.
 *
 * Alerts carry a code (`SETUP_TAXCODE_MISSING`, `INVOICES_OVERDUE`) and are
 * translated by it. The KPI labels and notes, and the type of each recent
 * record, arrive as English text with no code (dashboard.service.ts), so the
 * exact texts the API sends today are mapped to catalog keys here, the way
 * Setup Center maps its readiness messages, until the API sends codes (D22).
 * Anything not listed is shown as sent, never blank.
 */
import type { TFunction } from "@/lib/i18n";
import { recordTypeLabel, statusLabel } from "@/lib/i18n/records";

/** A Map, not an object literal: "constructor" must not match anything. */
const KPI_TEXT = new Map<string, (t: TFunction) => string>([
  ["Revenue (30 days)", (t) => t("home.kpis.revenue", { days: 30 })],
  ["Posted, sent and paid invoices", (t) => t("home.kpis.revenueMeta")],
  ["Receivables outstanding", (t) => t("home.kpis.receivables")],
  ["Nothing overdue", (t) => t("home.kpis.nothingOverdue")],
  ["Purchase commitments", (t) => t("home.kpis.commitments")],
  [
    "Approved and in-flight purchase orders",
    (t) => t("home.kpis.commitmentsMeta"),
  ],
  ["Open sales orders", (t) => t("home.kpis.openSalesOrders")],
  ["Not yet invoiced or cancelled", (t) => t("home.kpis.openSalesOrdersMeta")],
]);

/** `recentRecords[].type` as the API words it, to the record kind. */
const RECORD_TYPES = new Map<string, string>([
  ["Sales invoice", "SalesInvoice"],
  ["Sales order", "SalesOrder"],
  ["Purchase order", "PurchaseOrder"],
  ["Journal entry", "JournalEntry"],
]);

/** The number a message starts with ("3 overdue invoices."), if any. */
function leadingCount(text: string): number | undefined {
  const match = /^(\d+)\b/.exec(text);
  return match ? Number(match[1]) : undefined;
}

/** A KPI's label or note. */
export function kpiText(t: TFunction, text: string): string;
export function kpiText(
  t: TFunction,
  text: string | undefined,
): string | undefined;
export function kpiText(
  t: TFunction,
  text: string | undefined,
): string | undefined {
  if (text === undefined) return undefined;
  const known = KPI_TEXT.get(text);
  if (known) return known(t);
  const overdue = /^(\d+) overdue$/.exec(text);
  if (overdue) return t("home.kpis.overdue", { count: Number(overdue[1]) });
  return text;
}

/** A setup or attention alert, by its code. */
export function alertText(
  t: TFunction,
  alert: { code: string; message: string },
): string {
  const setup = /^SETUP_([A-Z]+)_MISSING$/.exec(alert.code);
  if (setup)
    return t(`home.setup.${setup[1]}`, { defaultValue: alert.message });
  const count = leadingCount(alert.message);
  switch (alert.code) {
    case "TENANT_PROVISIONING":
      return t("home.alerts.provisioning");
    case "INVOICES_OVERDUE":
      if (count !== undefined)
        return t("home.alerts.overdueInvoices", { count });
      break;
    case "APPROVALS_PENDING":
      if (count !== undefined)
        return t("home.alerts.approvalsPending", { count });
      break;
  }
  return alert.message;
}

/** A recent record's kind and status, as words. */
export function recentRecordText(
  t: TFunction,
  record: { type: string; status: string },
): { type: string; status: string } {
  const kind = RECORD_TYPES.get(record.type);
  if (!kind) return { type: record.type, status: record.status };
  return {
    type: recordTypeLabel(t, kind),
    status: statusLabel(t, kind, record.status),
  };
}
